import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import LinkIcon from "@mui/icons-material/Link";
import AUTH_CONTEXT from "../../../store";
import ARENO_API_ENDPOINT from "../../../api";

const HARDCODED_TAGS = [
  "Ankles",
  "Arms",
  "Back",
  "Chest",
  "Core",
  "Hamstrings",
  "Hips",
  "Knees",
  "Legs",
  "Lower Back",
  "Neck",
  "Shoulders",
  "Spine",
  "Upper Back",
];

const CHAR_LIMITS = { exerciseName: 50, benefits: 500, instructionStep: 100 };
const MAX_INSTRUCTIONS = 10;
const PRIMARY = "#4f005f";

const INSTRUCTION_PLACEHOLDERS = [
  "Lie flat on your back with knees bent and feet flat on the floor",
  "Place your hands behind your head, elbows wide",
  "Engage your core and slowly lift your shoulders off the ground",
  "Hold for 2-3 seconds, then slowly lower back down",
  "Keep your lower back pressed against the floor throughout",
  "Breathe out as you lift up, breathe in as you lower down",
  "Maintain control throughout the entire movement",
  "Focus on quality over quantity for each repetition",
  "Avoid pulling on your neck; keep the movement driven by your core",
  "Stop immediately if you feel any pain or discomfort",
];

const SectionCard = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "#fafafa",
    }}
  >
    <Typography
      variant="subtitle1"
      fontWeight={600}
      color="text.primary"
      mb={2}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

const AddExercise = () => {
  const navigate = useNavigate();
  const { token } = AUTH_CONTEXT();

  const [formData, setFormData] = useState({
    exerciseName: "",
    isRepBased: true,
    instructionSteps: ["", "", ""],
    benefits: "",
    tags: [],
    videoLink: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limit = CHAR_LIMITS[name];
    setFormData((prev) => ({
      ...prev,
      [name]: limit ? value.slice(0, limit) : value,
    }));
  };

  const handleInstructionChange = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      instructionSteps: prev.instructionSteps.map((s, i) =>
        i === index ? value.slice(0, CHAR_LIMITS.instructionStep) : s,
      ),
    }));
  };

  const addStep = () => {
    if (formData.instructionSteps.length < MAX_INSTRUCTIONS) {
      setFormData((prev) => ({
        ...prev,
        instructionSteps: [...prev.instructionSteps, ""],
      }));
    }
  };

  const removeStep = (index) => {
    if (formData.instructionSteps.length > 1) {
      setFormData((prev) => ({
        ...prev,
        instructionSteps: prev.instructionSteps.filter((_, i) => i !== index),
      }));
    }
  };

  const handleTagToggle = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const validate = () => {
    if (
      !formData.exerciseName.trim() ||
      formData.exerciseName.trim().length < 3
    ) {
      toast.error("Exercise name must be at least 3 characters");
      return false;
    }
    const validSteps = formData.instructionSteps.filter((s) => s.trim());
    if (validSteps.length === 0) {
      toast.error("Please enter at least one instruction step");
      return false;
    }
    if (validSteps.some((s) => s.trim().length < 10)) {
      toast.error("Each instruction step must be at least 10 characters");
      return false;
    }
    if (!formData.benefits.trim() || formData.benefits.trim().length < 10) {
      toast.error("Benefits must be at least 10 characters");
      return false;
    }
    if (formData.tags.length === 0) {
      toast.error("Please select at least one tag");
      return false;
    }
    if (!formData.videoLink.trim()) {
      toast.error("Please enter a video link");
      return false;
    }
    return true;
  };

  const convertInstructionsToHtml = (instructionSteps) => {
    const validSteps = instructionSteps.filter((step) => step.trim());
    if (validSteps.length === 0) return "";
    const items = validSteps.map((step, index) => `<h3>${index + 1}. ${step.trim()}</h3>`).join("<br>");
    return `<p>${items}</p>`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const response = await fetch(
        `${ARENO_API_ENDPOINT}/challenges/AdminCreateWorkoutLibraryWorkouts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            WorkoutName: formData.exerciseName.trim(),
            IsRepBased: formData.isRepBased,
            Instructions: convertInstructionsToHtml(formData.instructionSteps),
            About: formData.benefits.trim(),
            Tags: formData.tags,
            VideoURL: formData.videoLink.trim(),
          }),
        },
      );

      const data = await response.json();

      if (response.status === 200 && data.status === 200) {
        toast.success("Exercise created successfully!", {
          position: "bottom-left",
          autoClose: 3000,
          theme: "light",
        });
        setFormData({
          exerciseName: "",
          isRepBased: true,
          instructionSteps: ["", "", ""],
          benefits: "",
          tags: [],
          videoLink: "",
        });
      } else {
        toast.error(data.error || "Failed to create exercise", {
          position: "bottom-left",
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } catch (error) {
      toast.error(error.message, {
        position: "bottom-left",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", px: 3, py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          to="/dashboard"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Dashboard
        </Link>
        <Typography color="text.secondary">Exercise Services</Typography>
        <Typography color="text.primary" fontWeight={500}>
          Add Exercise
        </Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={4}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            backgroundColor: PRIMARY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FitnessCenterIcon sx={{ color: "#fff", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            lineHeight={1.2}
            color={"#181818ff"}
          >
            Add New Exercise
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fill in the details below to create a new exercise
          </Typography>
        </Box>
      </Stack>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Exercise Name */}
          <SectionCard title="Exercise Name">
            <TextField
              fullWidth
              name="exerciseName"
              label="Exercise Name"
              required
              value={formData.exerciseName}
              onChange={handleChange}
              placeholder="e.g. Seated Leg Press"
              helperText={`${formData.exerciseName.length} / ${CHAR_LIMITS.exerciseName} characters`}
              inputProps={{ maxLength: CHAR_LIMITS.exerciseName }}
            />
          </SectionCard>

          {/* Exercise Type */}
          <SectionCard title="Exercise Type">
            <FormControl>
              <RadioGroup
                row
                value={formData.isRepBased ? "true" : "false"}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isRepBased: e.target.value === "true",
                  }))
                }
              >
                <FormControlLabel
                  value="true"
                  control={
                    <Radio
                      sx={{
                        color: PRIMARY,
                        "&.Mui-checked": { color: PRIMARY },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Repetition Based
                      </Typography>
                    </Box>
                  }
                  sx={{ mr: 4 }}
                />
                <FormControlLabel
                  value="false"
                  control={
                    <Radio
                      sx={{
                        color: PRIMARY,
                        "&.Mui-checked": { color: PRIMARY },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Time Based
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </SectionCard>

          {/* Benefits */}
          <SectionCard title="About / Benefits">
            <TextField
              fullWidth
              multiline
              rows={5}
              name="benefits"
              label="About / Benefits"
              required
              value={formData.benefits}
              onChange={handleChange}
              placeholder={
                "1. Strengthens core muscles\n2. Improves posture\n3. Reduces lower back pain"
              }
              helperText={`${formData.benefits.length} / ${CHAR_LIMITS.benefits} characters`}
              inputProps={{ maxLength: CHAR_LIMITS.benefits }}
            />
          </SectionCard>

          {/* Instruction Steps */}
          <SectionCard
            title={`How to Do the Exercise (${formData.instructionSteps.length} / ${MAX_INSTRUCTIONS} steps)`}
          >
            <Typography variant="body2" color="text.secondary" mb={2}>
              Add clear, step-by-step instructions for performing this exercise.
            </Typography>
            <Stack spacing={2}>
              {formData.instructionSteps.map((step, index) => (
                <Stack
                  key={index}
                  direction="row"
                  alignItems="flex-start"
                  spacing={1.5}
                >
                  <Box
                    sx={{
                      minWidth: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: PRIMARY,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      mt: 1,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    value={step}
                    onChange={(e) =>
                      handleInstructionChange(index, e.target.value)
                    }
                    placeholder={
                      INSTRUCTION_PLACEHOLDERS[index] ||
                      `Enter step ${index + 1} instruction`
                    }
                    helperText={`${step.length} / ${CHAR_LIMITS.instructionStep}`}
                    inputProps={{ maxLength: CHAR_LIMITS.instructionStep }}
                  />
                  {formData.instructionSteps.length > 1 && (
                    <IconButton
                      onClick={() => removeStep(index)}
                      size="small"
                      sx={{
                        mt: 0.5,
                        color: "error.main",
                        border: "1px solid",
                        borderColor: "error.light",
                        borderRadius: 1.5,
                        "&:hover": { backgroundColor: "error.lighter" },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>

            {formData.instructionSteps.length < MAX_INSTRUCTIONS && (
              <Button
                startIcon={<AddIcon />}
                onClick={addStep}
                variant="outlined"
                size="small"
                sx={{
                  mt: 2,
                  borderStyle: "dashed",
                  color: PRIMARY,
                  borderColor: PRIMARY,
                  "&:hover": {
                    borderColor: PRIMARY,
                    backgroundColor: `${PRIMARY}10`,
                  },
                }}
              >
                Add Step
              </Button>
            )}
          </SectionCard>

          {/* Tags */}
          <SectionCard title="Tags">
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select all tags that apply to this exercise.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {HARDCODED_TAGS.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  clickable
                  onClick={() => handleTagToggle(tag)}
                  variant={formData.tags.includes(tag) ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 500,
                    ...(formData.tags.includes(tag)
                      ? {
                          backgroundColor: PRIMARY,
                          color: "#fff",
                          "&:hover": { backgroundColor: PRIMARY },
                        }
                      : {
                          borderColor: PRIMARY,
                          color: PRIMARY,
                          "&:hover": { backgroundColor: `${PRIMARY}10` },
                        }),
                  }}
                />
              ))}
            </Box>
            {formData.tags.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                mt={1.5}
                display="block"
              >
                Selected: {formData.tags.join(", ")}
              </Typography>
            )}
          </SectionCard>

          {/* Video Link */}
          <SectionCard title="Video Link">
            <TextField
              fullWidth
              name="videoLink"
              label="Video URL"
              required
              type="url"
              value={formData.videoLink}
              onChange={handleChange}
              placeholder="https://www.youtube.com/watch?v=example"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </SectionCard>

          <Divider />

          {/* Actions */}
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate(-1)}
              disabled={isLoading}
              sx={{ px: 4, py: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                px: 4,
                backgroundColor: PRIMARY,
                color: "#fff",
                "&:hover": { backgroundColor: "#3a0047" },
              }}
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {isLoading ? "Creating..." : "Create Exercise"}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default AddExercise;
